/**
 * Generate Text readiness — optional context assets + user prompt + provider + skill.
 *
 * Valid when:
 * - prompt-only (0 assets, non-empty additionalInstruction), or
 * - asset-backed (≥1 ready image), with empty instruction allowed on that path.
 */

import type { WorkspaceProviderOption } from '../../../api'
import type { GenerateTextSkillLike } from './generateTextSkillFilter'
import { normalizeGenerateTextFieldDefaults } from './generateTextContract'
import {
  findProviderOptionById,
  isProviderOptionSelectable,
  normalizeProviderSelectionMode,
  resolveCapabilityProviderOptionId,
} from './capabilityProviderSelection'
import {
  buildGenerationReadiness,
  type GenerationReadiness,
} from './generationReadiness'
import type { GenerateTextInputAsset } from './generateTextContract'
import {
  filterImageCompatibleTextSkills,
  findTextGenerationSkill,
  GENERATE_TEXT_IMAGE_REQUIRED_SKILL_KEYS,
  resolveGenerateTextSkill,
  skillEligibleForPromptOnlyGenerateText,
} from './generateTextSkillFilter'
import type { InputMaterial } from './dataflow'
import { isVideoPromptEnhancementPurpose } from './videoPromptEnhancement'
import {
  allGenerateTextReferenceImagesReady,
  countGenerateTextReferenceImages,
  isGenerateTextReferenceImageCountOverLimit,
} from './generateTextImageInput'

export function parseGenerateTextInputAssets(fields: Record<string, unknown>): GenerateTextInputAsset[] {
  if (!Array.isArray(fields.inputAssets)) return []
  const out: GenerateTextInputAsset[] = []
  for (const raw of fields.inputAssets) {
    if (!raw || typeof raw !== 'object') continue
    const row = raw as Record<string, unknown>
    const assetId = typeof row.assetId === 'string' ? row.assetId.trim() : ''
    const generatedOutputId =
      typeof row.generatedOutputId === 'string' ? row.generatedOutputId.trim() : ''
    if (!assetId && !generatedOutputId) continue
    out.push({
      assetId: assetId || generatedOutputId,
      assetType: typeof row.assetType === 'string' && row.assetType.trim() ? row.assetType.trim() : 'image',
      sourceNodeId: typeof row.sourceNodeId === 'string' ? row.sourceNodeId : null,
      displayName: typeof row.displayName === 'string' ? row.displayName : null,
      role: typeof row.role === 'string' ? row.role : null,
      generatedOutputId: generatedOutputId || null,
    })
  }
  return out
}

/** Prefer live edge materials; fall back to persisted inputAssets field. */
export function resolveGenerateTextImageInputs(args: {
  fields: Record<string, unknown>
  inputMaterials?: InputMaterial[] | null
}): Array<{
  assetId: string | null
  generatedOutputId: string | null
  ready: boolean
  sourceNodeId?: string | null
  displayName?: string | null
}> {
  const materials = (args.inputMaterials || []).filter((m) => m.kind === 'IMAGE')
  if (materials.length > 0) {
    return materials.map((m) => ({
      assetId: 'entityId' in m && m.entityId ? m.entityId : null,
      generatedOutputId: 'generatedOutputId' in m && m.generatedOutputId ? m.generatedOutputId : null,
      ready: Boolean(m.ready),
      sourceNodeId: m.nodeId,
      displayName: m.sourceLabel,
    }))
  }
  return parseGenerateTextInputAssets(args.fields).map((a) => ({
    assetId: a.assetId || null,
    generatedOutputId: a.generatedOutputId || null,
    ready: Boolean(a.assetId || a.generatedOutputId),
    sourceNodeId: a.sourceNodeId ?? null,
    displayName: a.displayName ?? null,
  }))
}

/** User-authored generation intent from the supplemental instruction field. */
export function hasGenerateTextUserPrompt(fields: Record<string, unknown>): boolean {
  const { additionalInstruction } = normalizeGenerateTextFieldDefaults(fields)
  return Boolean(additionalInstruction.trim())
}

export function evaluateGenerateTextInputReadiness(args: {
  fields: Record<string, unknown>
  inputMaterials?: InputMaterial[] | null
}): GenerationReadiness {
  const imageCount = countGenerateTextReferenceImages(args)
  if (isGenerateTextReferenceImageCountOverLimit(imageCount)) {
    return buildGenerationReadiness({
      blockingReasons: [
        {
          code: 'GENERATION_INPUT_REQUIRED',
          field: 'input_assets',
          messageKey: 'generateText.readiness.tooManyReferenceImages',
          dimension: 'INPUT_READY',
        },
      ],
    })
  }

  const images = resolveGenerateTextImageInputs(args)

  // Bound video prompt enhancement: prompt context is stamped from the parent
  // Video node; images are optional references (warn only, never block).
  if (isVideoPromptEnhancementPurpose(args.fields.purpose)) {
    if (images.length === 0) {
      return buildGenerationReadiness({
        warnings: [
          {
            code: 'INPUT_ASSET_OPTIONAL',
            field: 'input_assets',
            messageKey: 'generateText.readiness.inputAssetOptional',
            dimension: 'INPUT_READY',
          },
        ],
      })
    }
    const anyReady = images.some((img) => img.ready && (img.assetId || img.generatedOutputId))
    if (!anyReady) {
      return buildGenerationReadiness({
        blockingReasons: [
          {
            code: 'INPUT_ASSET_NOT_READY',
            field: 'input_assets',
            messageKey: 'generateText.readiness.inputAssetNotReady',
            dimension: 'INPUT_READY',
          },
        ],
      })
    }
    return buildGenerationReadiness({})
  }

  const readyTextMaterials = (args.inputMaterials || []).filter(
    (m) => m.kind === 'TEXT' && m.ready && String(m.text || '').trim().length > 0,
  )

  if (images.length === 0) {
    // Connected TEXT materials (e.g. text_asset → generate_text) are valid
    // generation context — same as image refs: additionalInstruction optional.
    if (readyTextMaterials.length > 0) {
      return buildGenerationReadiness({})
    }
    if (hasGenerateTextUserPrompt(args.fields)) {
      return buildGenerationReadiness({})
    }
    return buildGenerationReadiness({
      blockingReasons: [
        {
          code: 'PROMPT_REQUIRED',
          field: 'additional_instruction',
          messageKey: 'generateText.readiness.promptRequired',
          dimension: 'INPUT_READY',
        },
      ],
    })
  }
  if (!allGenerateTextReferenceImagesReady(args)) {
    return buildGenerationReadiness({
      blockingReasons: [
        {
          code: 'INPUT_ASSET_NOT_READY',
          field: 'input_assets',
          messageKey: 'generateText.readiness.inputAssetNotReady',
          dimension: 'INPUT_READY',
        },
      ],
    })
  }
  // additionalInstruction is USER_OPTIONAL — empty must never block.
  return buildGenerationReadiness({})
}

export function evaluateGenerateTextProviderReadiness(args: {
  fields: Record<string, unknown>
  providerOptions: WorkspaceProviderOption[]
  loading: boolean
  error: boolean
}): GenerationReadiness {
  if (args.loading) return buildGenerationReadiness({})
  if (args.error || args.providerOptions.length === 0) {
    return buildGenerationReadiness({
      blockingReasons: [
        {
          code: 'NO_AVAILABLE_PROVIDER',
          messageKey: 'generateText.readiness.noAvailableProvider',
          dimension: 'PROVIDER_READY',
        },
      ],
    })
  }
  const anyAvailable = args.providerOptions.some(isProviderOptionSelectable)
  if (!anyAvailable) {
    return buildGenerationReadiness({
      blockingReasons: [
        {
          code: 'NO_AVAILABLE_PROVIDER',
          messageKey: 'generateText.readiness.noAvailableProvider',
          dimension: 'PROVIDER_READY',
        },
      ],
    })
  }
  const selectionMode = normalizeProviderSelectionMode(args.fields.providerSelectionMode)
  const resolvedId = resolveCapabilityProviderOptionId({
    selectionMode,
    storedProviderOptionId: (args.fields.providerOptionId as string | null) ?? null,
    storedProviderDisplayName: (args.fields.providerDisplayName as string | null) ?? null,
    options: args.providerOptions,
  })
  const resolved = findProviderOptionById(args.providerOptions, resolvedId)
  if (!resolved || !isProviderOptionSelectable(resolved)) {
    return buildGenerationReadiness({
      blockingReasons: [
        {
          code: 'NO_AVAILABLE_PROVIDER',
          messageKey: 'generateText.readiness.noAvailableProvider',
          dimension: 'PROVIDER_READY',
        },
      ],
    })
  }
  return buildGenerationReadiness({})
}

export function evaluateGenerateTextSkillReadiness(args: {
  fields: Record<string, unknown>
  skills: GenerateTextSkillLike[]
  skillsLoading: boolean
  skillsError: boolean
  sourceNodeType?: string | null
  inputMaterials?: InputMaterial[] | null
}): GenerationReadiness {
  if (args.skillsLoading) return buildGenerationReadiness({})
  const images = resolveGenerateTextImageInputs({
    fields: args.fields,
    inputMaterials: args.inputMaterials,
  })
  const imageInputCount = images.filter((img) => img.assetId || img.generatedOutputId).length
  const compatible =
    imageInputCount > 0
      ? filterImageCompatibleTextSkills(args.skills)
      : args.skills.filter(skillEligibleForPromptOnlyGenerateText)
  if (args.skillsError && compatible.length === 0) {
    return buildGenerationReadiness({
      blockingReasons: [
        {
          code: 'SKILL_UNAVAILABLE',
          field: 'skill_key',
          messageKey: 'generateText.readiness.skillUnavailable',
          dimension: 'SKILL_READY',
        },
      ],
    })
  }
  if (compatible.length === 0) {
    return buildGenerationReadiness({
      blockingReasons: [
        {
          code: 'SKILL_UNAVAILABLE',
          field: 'skill_key',
          messageKey: 'generateText.readiness.skillUnavailable',
          dimension: 'SKILL_READY',
        },
      ],
    })
  }
  const source = String(args.fields.skillSelectionSource || 'AUTO').toUpperCase()
  const skillKey = (args.fields.skillKey as string | null) ?? null
  if (
    imageInputCount === 0 &&
    source === 'EXPLICIT' &&
    skillKey &&
    GENERATE_TEXT_IMAGE_REQUIRED_SKILL_KEYS.has(skillKey)
  ) {
    return buildGenerationReadiness({
      blockingReasons: [
        {
          code: 'GENERATION_INPUT_REQUIRED',
          field: 'input_assets',
          messageKey: 'generateText.readiness.imageSkillRequiresAsset',
          dimension: 'SKILL_READY',
        },
      ],
    })
  }
  if (source === 'EXPLICIT' && skillKey && !findTextGenerationSkill(compatible, skillKey)) {
    return buildGenerationReadiness({
      blockingReasons: [
        {
          code: 'SKILL_UNAVAILABLE',
          field: 'skill_key',
          messageKey: 'generateText.readiness.skillUnavailableReselect',
          dimension: 'SKILL_READY',
        },
      ],
    })
  }
  const resolved = resolveGenerateTextSkill({
    skillSelectionSource: source,
    skillKey,
    skills: args.skills,
    sourceNodeType: args.sourceNodeType,
    preferredSkillKey: (args.fields.preferredSkillKey as string | null) ?? null,
    imageInputCount,
  })
  if (!resolved) {
    return buildGenerationReadiness({
      blockingReasons: [
        {
          code: 'SKILL_UNAVAILABLE',
          field: 'skill_key',
          messageKey: 'generateText.readiness.skillUnavailable',
          dimension: 'SKILL_READY',
        },
      ],
    })
  }
  return buildGenerationReadiness({})
}

export function evaluateGenerateTextRuntimeReadiness(isBusy: boolean): GenerationReadiness {
  if (!isBusy) return buildGenerationReadiness({})
  return buildGenerationReadiness({
    blockingReasons: [
      {
        code: 'RUNTIME_BUSY',
        messageKey: 'generateText.readiness.runtimeBusy',
        dimension: 'RUNTIME_READY',
      },
    ],
  })
}

/** Full pre-submit gate for Generate Text. */
export function evaluateGenerateTextReadiness(args: {
  fields: Record<string, unknown>
  inputMaterials?: InputMaterial[] | null
  providerOptions: WorkspaceProviderOption[]
  providerLoading: boolean
  providerError: boolean
  skills: GenerateTextSkillLike[]
  skillsLoading: boolean
  skillsError: boolean
  isBusy: boolean
  sourceNodeType?: string | null
}): GenerationReadiness {
  const input = evaluateGenerateTextInputReadiness({
    fields: args.fields,
    inputMaterials: args.inputMaterials,
  })
  const provider = evaluateGenerateTextProviderReadiness({
    fields: args.fields,
    providerOptions: args.providerOptions,
    loading: args.providerLoading,
    error: args.providerError,
  })
  const skill = evaluateGenerateTextSkillReadiness({
    fields: args.fields,
    skills: args.skills,
    skillsLoading: args.skillsLoading,
    skillsError: args.skillsError,
    sourceNodeType: args.sourceNodeType,
    inputMaterials: args.inputMaterials,
  })
  const runtime = evaluateGenerateTextRuntimeReadiness(args.isBusy)
  return buildGenerationReadiness({
    blockingReasons: [
      ...input.blockingReasons,
      ...provider.blockingReasons,
      ...skill.blockingReasons,
      ...runtime.blockingReasons,
    ],
    warnings: [...input.warnings, ...provider.warnings, ...skill.warnings, ...runtime.warnings],
  })
}
